import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CaretUpDown, Check, GearSix, Plus, Users } from '@phosphor-icons/react';
import { workspacesApi } from '@/api/endpoints';
import { workspaceStore } from '@/auth/stores';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useAuth, useWorkspace } from '@/hooks/useAuth';
import { useCurrentWorkspace } from '@/hooks/useLibraries';
import { cn } from '@/lib/utils';
import type { WorkspaceSummary } from '@/types/api';
import { safeHttpsUrl } from '@/utils/mediaKit';

/**
 * Which workspace the app is showing, and the way to change it.
 *
 * It used to be a bare name with a caret, centred in a wide button — most
 * people never guessed it was a switcher, or what "ZZ Shop" referred to. It
 * now says "Workspace" above the name, carries the workspace's mark, and the
 * menu opens with one line on what a workspace is.
 */
export function WorkspaceSwitcher() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refresh } = useAuth();
  const { workspaces, active, switchTo, isAdmin } = useWorkspace();
  const current = useCurrentWorkspace();
  const logo = safeHttpsUrl(current.data?.logoUrl);

  const [createOpen, setCreateOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [creating, setCreating] = useState(false);

  const createWorkspace = async () => {
    if (!newWorkspaceName.trim()) return;

    setCreating(true);
    try {
      const workspace = await workspacesApi.create({
        name: newWorkspaceName.trim(),
      });
      workspaceStore.set(workspace.id);
      queryClient.clear();
      await refresh();
      setCreateOpen(false);
      setNewWorkspaceName('');
      navigate('/app');
      toast.success(`${workspace.name} is ready`);
    } catch (error: any) {
      toast.error(error?.message ?? 'Could not create the workspace');
    } finally {
      setCreating(false);
    }
  };

  const name = active?.name ?? 'No workspace';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Workspace: ${name}. Switch workspace`}
            className="flex h-10 min-w-0 max-w-[240px] cursor-pointer items-center gap-2.5 rounded-lg border border-border bg-background py-1 pl-1 pr-2 text-left shadow-xs transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted/50 sm:max-w-[280px]"
          >
            <WorkspaceMark name={name} logo={logo} />
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Workspace
              </span>
              <span className="truncate text-sm font-semibold text-foreground">
                {name}
              </span>
            </span>
            <CaretUpDown size={14} className="ml-auto shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-[min(18rem,calc(100vw-1rem))] p-1.5">
          <div className="px-2 pb-2 pt-1.5">
            <p className="m-0 text-xs font-semibold text-foreground">Switch workspace</p>
            <p className="m-0 mt-0.5 text-xs text-muted-foreground">
              Each workspace has its own accounts, posts and team.
            </p>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {workspaces.map((workspace) => {
              const selected = workspace.id === active?.id;
              return (
                <DropdownMenuItem
                  key={workspace.id}
                  onClick={() => {
                    if (selected) return;
                    switchTo(workspace.id);
                    navigate('/app');
                  }}
                  className={cn('gap-2.5 py-2', selected && 'bg-muted/50')}
                >
                  <WorkspaceMark
                    name={workspace.name}
                    logo={selected ? logo : null}
                    size="sm"
                  />
                  <span className="flex min-w-0 flex-1 flex-col leading-tight">
                    <span className="truncate text-sm font-medium text-foreground">
                      {workspace.name}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {describe(workspace)}
                    </span>
                  </span>
                  {selected && (
                    <Check size={15} weight="bold" className="shrink-0 text-primary" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </div>

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/app/settings/workspace')}>
            <GearSix size={15} />
            <span>Workspace settings</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/app/settings/members')}>
            <Users size={15} />
            <span>{isAdmin ? 'Invite teammates' : 'Members'}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setCreateOpen(true)}>
            <Plus size={15} />
            <span>New workspace</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New workspace</DialogTitle>
            <DialogDescription>
              A workspace has its own posts, media and connected accounts.
              Nothing is shared between them — useful for a second brand or a
              client.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label
              htmlFor="new-workspace-name"
              className="text-xs font-medium text-muted-foreground mb-1 block"
            >
              Workspace name
            </label>
            <Input
              id="new-workspace-name"
              value={newWorkspaceName}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              placeholder="Acme Marketing"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') createWorkspace();
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              onClick={createWorkspace}
              loading={creating}
              disabled={!newWorkspaceName.trim()}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** The workspace's logo when it has one, its initials when it does not. */
function WorkspaceMark({
  name,
  logo,
  size = 'md',
}: {
  name: string;
  logo: string | null;
  size?: 'sm' | 'md';
}) {
  const box = size === 'md' ? 'size-8 text-xs' : 'size-7 text-[11px]';
  if (logo) {
    return (
      <img
        src={logo}
        alt=""
        className={cn(box, 'shrink-0 rounded-md border border-border bg-background object-contain')}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        box,
        'flex shrink-0 items-center justify-center rounded-md bg-primary/15 font-bold text-primary'
      )}
    >
      {initials(name)}
    </span>
  );
}

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

function describe(workspace: WorkspaceSummary): string {
  const role = workspace.role.charAt(0) + workspace.role.slice(1).toLowerCase();
  const members = `${workspace.memberCount} member${workspace.memberCount === 1 ? '' : 's'}`;
  return `${role} · ${members}`;
}
